import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import Lightbox from "@/components/Lightbox";
import { Camera, ArrowRight } from "lucide-react";

export default function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [parks, setParks] = useState([]);
  const [park, setPark] = useState("All");
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    api.get("/gallery").then((r) => {
      setPhotos(r.data.photos || []);
      setParks(r.data.parks || []);
    }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const visible = useMemo(
    () => (park === "All" ? photos : photos.filter((p) => p.parks.includes(park))),
    [photos, park]
  );

  return (
    <div data-testid="gallery-page" className="bg-[#FAF6F0]">
      <section className="pt-36 md:pt-44 pb-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="overline">the gallery</p>
          <h1 className="font-display text-5xl md:text-7xl italic font-light mt-4 leading-[1.04] max-w-4xl">
            Tanzania, Park by Park
          </h1>
          <p className="mt-6 text-[#23170E]/65 max-w-2xl leading-relaxed">
            Every frame below comes from the parks we actually guide in — the Serengeti short grass plains,
            the Ngorongoro crater floor, the baobab country of Tarangire and the beaches of Zanzibar.
            Pick a park to narrow it down.
          </p>

          <div className="mt-10 flex flex-wrap gap-2.5">
            {[{ name: "All", count: photos.length }, ...parks].map((p) => (
              <button
                key={p.name}
                data-testid={`gallery-filter-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setPark(p.name)}
                className={`rounded-full px-5 py-2.5 text-sm transition-colors ${
                  park === p.name
                    ? "bg-[#1F150E] text-[#FAF6F0]"
                    : "bg-[#FFFFFF] text-[#23170E]/75 border border-[#E2D5C3] hover:border-[#A6521B]/50 hover:text-[#A6521B]"
                }`}
              >
                {p.name} <span className="opacity-55">{p.count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {visible.length === 0 ? (
            <div data-testid="gallery-empty" className="border border-dashed border-[#23170E]/20 rounded-3xl p-16 text-center text-[#23170E]/55">
              <Camera className="mx-auto mb-3" size={28} />
              No photos for this park yet.
            </div>
          ) : (
            <div data-testid="gallery-grid" className="gallery-cols">
              {visible.map((p, i) => (
                <button
                  key={p.url}
                  data-testid={`gallery-photo-${i}`}
                  onClick={() => { setIndex(i); setOpen(true); }}
                  className="group relative block w-full mb-5 overflow-hidden rounded-2xl bg-[#F1E8DC] text-left"
                >
                  <img
                    src={p.url}
                    alt={p.caption || "Tanzania safari"}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-[#18100A]/85 via-[#18100A]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="absolute inset-x-0 bottom-0 p-5 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {p.caption && <span className="block font-display italic text-[#FAF6F0] text-lg leading-snug">{p.caption}</span>}
                    <span className="block mt-1.5 text-[11px] uppercase tracking-[0.18em] text-[#C88B35]">
                      {p.parks.join(" · ")}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="mt-16 text-center">
            <p className="font-display text-3xl md:text-4xl italic font-light">Want to shoot your own?</p>
            <Link to="/tours" data-testid="gallery-tours-cta" className="inline-flex mt-7 btn-pill btn-pill-primary">
              Browse Safaris <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Lightbox images={visible} open={open} index={index} onClose={() => setOpen(false)} />

      <style>{`
        .gallery-cols { column-count: 1; column-gap: 20px; }
        @media (min-width: 640px) { .gallery-cols { column-count: 2; } }
        @media (min-width: 1024px) { .gallery-cols { column-count: 3; } }
        @media (min-width: 1440px) { .gallery-cols { column-count: 4; } }
      `}</style>
    </div>
  );
}
